import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ModalComponent } from '../modal/modal.component';
import { IconComponent } from '../../icons/icon.component';
import { PatientsApiService } from '../../../core/services/patients/patients-api.service';

interface PatientOption {
  id: string;
  name: string;
  contact_number: string;
  patient_pid: string;
  telecaller: string;
}

/**
 * "Book Medicine" entry modal.
 * Search a patient by mobile number → pick one to open the order page with that patient,
 * or start the order for a new patient (the order page opens with "New Patient?" on).
 */
@Component({
  selector: 'app-order-booking-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent, IconComponent],
  templateUrl: './order-booking-modal.component.html',
})
export class OrderBookingModalComponent implements OnDestroy {
  @Input() isOpen = false;
  @Output() closed = new EventEmitter<void>();

  mobile = '';
  patients: PatientOption[] = [];
  isSearching = false;
  hasSearched = false;
  errorMessage = '';

  private readonly orderRoute = '/orders/medicine-orders/create';
  private search$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(private router: Router, private patientsApi: PatientsApiService) {
    this.search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(term => {
          this.errorMessage = '';
          this.isSearching = term.length > 0;
          if (!term) { this.patients = []; this.hasSearched = false; }
        }),
        switchMap(term => term
          ? this.patientsApi.getPatients(1, 20, term).pipe(
            catchError(() => { this.errorMessage = 'Could not search patients. Please try again.'; return of(null); }))
          : of(null)),
        takeUntil(this.destroy$),
      )
      .subscribe(res => {
        this.isSearching = false;
        if (!this.mobile.trim()) return;
        const data = res?.data || res || {};
        const list = data?.patients || (Array.isArray(data) ? data : []);
        this.patients = list.map((p: any) => ({
          id: p._id || p.id || '',
          name: p.name || 'Unknown',
          contact_number: p.contact_number || '',
          patient_pid: p.patient_pid || p.patient_id || '',
          telecaller: (typeof p.telecaller === 'object' ? p.telecaller?.name : p.telecaller) || p.telecaller_name || '',
        }));
        this.hasSearched = !this.errorMessage;
      });
  }

  onMobileChange(value: string): void {
    this.mobile = (value || '').replace(/[^\d+]/g, '');
    this.search$.next(this.mobile.trim());
  }

  selectPatient(p: PatientOption): void {
    this.close();
    this.router.navigate([this.orderRoute], { queryParams: { patientId: p.id } });
  }

  newPatient(): void {
    const mobile = this.mobile.trim();
    this.close();
    this.router.navigate([this.orderRoute], { queryParams: { newPatient: 'true', ...(mobile ? { mobile } : {}) } });
  }

  close(): void {
    this.mobile = '';
    this.patients = [];
    this.hasSearched = false;
    this.errorMessage = '';
    this.closed.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
